import React, { useEffect, useState } from "react";
import { HTTP } from "../../api/resources";
import Select from "react-select";
import SponsorshipForm from "./SponsorshipForm";
import { all as translations } from "../../modules/translations/translations";

export default function SponsorshipTable({
  donorId,
  allBeneficiaries,
  beneficiaryIds,
  sponsorships,
  forManager = false,
  title = "Sponsorships",
  withProjects = false,
  disabled = false,
  forBeneficiary = false,
  projectFromDonor = false,
  forCommunities = false,
  hasEditPermission = true,
}) {
  const [editSponsorships, setEditSponsorships] = useState(sponsorships);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [selectOptions, setSelectOptions] = useState(null)
  const [showList, setShowList] = useState(false)

  const [editingSponsorship, setEditingSponsorship] = useState(null);
  const [openCustomFieldsForm, setOpenCustomFieldsForm] = useState(false);

  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const [unsavedIds, setUnsavedIds] = useState([])
  const [allUsers, setAllUsers] = useState(false)
  const [totalSelection, setTotalSelection] = useState(editSponsorships)

  useEffect(() => {
    initializeSelectOptions()
    setUnsavedIds([])
  }, []);

  useEffect(() => {
    setShowList(true)
  }, [showList])

  const addBeneficiary = () => {
    if (selectedBeneficiary) {
      setEditSponsorships((state) => [
        ...state,
        { beneficiary_id: selectedBeneficiary.id },
      ]);
      unsavedIds.push(selectedBeneficiary.id)
    }
    setSelectedBeneficiary(null)
    removeSelectOption(selectedBeneficiary)
  };

  const updateSponsorship = (sponsorship) => {
    let updatedSponsorships = editSponsorships.map((editSponsorship, idx) => {
      if (editSponsorship.id === sponsorship.id) {
        return sponsorship;
      } else {
        return editSponsorship;
      }
    });

    setEditSponsorships(updatedSponsorships);
  };

  const removeFromUnsaved = (id) => {
    if (id < 0) return undefined
    for (let x = 0; x < unsavedIds.length; x++) {
      let value = unsavedIds[x]
      if (value == id) {
        unsavedIds.splice(x, 1)
      }
    }
  }

  const removeBeneficiary = (id) => {
    const updatedSponsorships = _.reject(
      editSponsorships,
      (s) => s.beneficiary_id == id
    );
    removeFromUnsaved(id)
    setEditSponsorships(updatedSponsorships);
    addSelectOption(id)
  };

  const beneficiaryNameFromId = (id) => {
      let beneficiary = _.find(allBeneficiaries, { id: id });
      if (beneficiary) {
        if (projectFromDonor) return beneficiary.name
        else return beneficiary.first_name + " " + beneficiary.last_name;
      } else {
        return "Unknown beneficiary";
      }
  };

  const save = () => {
    setSaving(true);
    const beneficiaryIds = _.map(editSponsorships, "beneficiary_id");
    const data = {
      sponsorships: {
        donor_id: donorId,
        beneficiary_ids: beneficiaryIds,
      },
    };

    HTTP.put(`/d/${donorId}/update_sponsorships`, data)
      .then((response) => {
        setSaving(false);
        setUnsavedIds([])
        setEditSponsorships(response.data);
        Alert.show("notice", "Sponsorships saved!");
      })
      .catch((e) => {
        setSaving(false);
        Alert.show("error", "Sponsorships not saved! Try again or refresh.");
      });
  };

  const handleDelete = async (sponsorship) => {
    removeBeneficiary(sponsorship.beneficiary_id);
    if (!sponsorship.id) {
      return;
    }

    if (forManager) {
      try {
        await updateManagerList(sponsorship)
      } catch {
        Alert.show('error', 'Could not update manager list')
        return
      }

      Alert.show('notice', 'Removed successfully')
      return
    }

    if (withProjects) {
      try {
        await updateProjectList(sponsorship)
      } catch {
        Alert.show('error', 'Could not update sponsors list')
        return
      }

      Alert.show('notice', 'Removed successfully')
      return
    }

    if (forBeneficiary) {
      try {
        await updateBeneficiaryList(sponsorship)
      } catch {
        Alert.show('error', 'Could not update list')
        return
      }

      Alert.show('notice', 'Removed successfully')
      return
    }

    if (projectFromDonor) {
      try {
        await updateDonorProjectList(sponsorship)
      } catch {
        Alert.show('error', 'Could not update list')
        return
      }

      Alert.show('notice', 'Removed successfully')
      return
    }

    if (forCommunities) {
      try {
        await updateMemberships(sponsorship)
      } catch {
        Alert.show('error', 'Could not update memberships')
        return
      }

      Alert.show('notice', 'Member removed successfully')
      return
    }

    HTTP.delete(`/sponsorships/${sponsorship.id}`)
      .then((response) => {
        Alert.show("notice", "Sponsorship removed!");
      })
      .catch((e) => {
        Alert.show("error", "Could not remove. Try again or refresh.");
        setSaving(false);
      });
  };

  const updateMemberships = (data) => {
    return HTTP.delete(`/community/membership/${donorId}/${data.beneficiary_id}`)
  }

  const updateManagerList = (data) => {
    return HTTP.delete(`/sponsorship/groups/${donorId}/${data.beneficiary_id}`)
  }

  const updateDonorProjectList = (data) => {
    return HTTP.delete(`/pro/sponsors/d/${data.beneficiary_id}/${donorId}`)
  }

  const updateProjectList = (data) => {
    return HTTP.delete(`/pro/sponsors/delete/${donorId}/${data.beneficiary_id}`)
  }

  const updateBeneficiaryList = (data) => {
    return HTTP.delete(`/b/sponsors/${donorId}/${data.beneficiary_id}`)
  }

  const showCustomFieldsForm = (beneficiaryId) => {
    let sponsorship = _.find(editSponsorships, {
      beneficiary_id: beneficiaryId,
    });
    setEditingSponsorship(sponsorship);
    setOpenCustomFieldsForm(true);
  };

  const handleConfirmDelete = (sponsorship) => {
    if (sponsorship.id) {
      setConfirmingDeleteId(sponsorship.id);
    } else {
      handleDelete(sponsorship);
    }
  };

  const initializeSelectOptions = () => {
    let updatedOptions = _.differenceWith(allBeneficiaries, editSponsorships, (a, b) => a.id == b.beneficiary_id)
    updatedOptions = _.orderBy(updatedOptions, ['first_name'], ['asc'])
    setSelectOptions(updatedOptions)
  }

  const addSelectOption = (option) => {
    let beneficiary = _.find(allBeneficiaries, {'id': option})
    if (beneficiary) {
      let updatedOptions = [...selectOptions, beneficiary]
      updatedOptions = _.orderBy(updatedOptions, ['first_name'], ['asc'])
      setSelectOptions(updatedOptions)
    }
  }

  const removeSelectOption = (option) => {
    let updatedOptions = _.reject(selectOptions, (a) => a.id == option.id)
    updatedOptions = _.orderBy(updatedOptions, ['first_name'], ['asc'])
    setSelectOptions(updatedOptions)
  }

  const clearFromList = () => {
    if (unsavedIds.length == 0) return undefined
    for (let x = 0; x < unsavedIds.length; x++) {
      let id = unsavedIds[id]
      for (let j = 0; j < editSponsorships.length; j++) {
        if (id == editSponsorships[j].id) {
          addSelectOption(editSponsorships[j].id)
          editSponsorships.splice(j, 1)
        }
      }
    }
  }

  const cleanList = () => {
    setShowList(false)
    setEditing(false)
    clearFromList()
    if (unsavedIds.length > 0) Alert.show("notice", `${unsavedIds.length} unsaved selections have been removed from the list`)
    setUnsavedIds([])
    initializeSelectOptions()
  }

  const saveBeneficiaries = async () => {
    setSaving(true)
    let beneficiaryIds = []
    editSponsorships.map(beneficiary => beneficiaryIds.push(beneficiary.beneficiary_id))
    let data = {
      manager_id: donorId,
      beneficiary_ids: beneficiaryIds
    }

    let response = null
    try {
      response = await updateIds(data)
    } catch {
      Alert.show('error', "Failed to update list, try again")
      setSaving(false)
      return
    }

    Alert.show('Notice', "Updated successfully")
    setSaving(false);
    setConfirmingDeleteId(false)
    setUnsavedIds([])
    setEditSponsorships(response.data)
  }

  const updateIds = (data) => {
    return HTTP.patch(`/sponsorship/groups`, data)
  }

  const updateProjectSponsors = async () => {
    setSaving(true)
    let sponsorIds = []
    editSponsorships.map(beneficiary => sponsorIds.push(beneficiary.beneficiary_id))
    let data = {
      sponsor_ids: sponsorIds
    }

    let response = null
    try {
      response = await projectSponsor(data)
    } catch {
      Alert.show('error', "Failed, try again")
      setSaving(false)
      return
    }

    Alert.show('Notice', "Updated successfully")
    setSaving(false);
    setConfirmingDeleteId(false)
    setUnsavedIds([])
    setEditSponsorships(response.data)
  }

  const projectSponsor = (data) => {
    return HTTP.patch(`/pro/sponsors/${donorId}`, data)
  }

  const saveDonors = async () => {
    setSaving(true)

    let donorIds = []
    editSponsorships.map(beneficiary => donorIds.push(beneficiary.beneficiary_id))
    let data = {
      donor_ids: donorIds
    }

    let response = null
    try {
      response = await updateDonors(data)
    } catch {
      Alert.show('error', "Failed to update, try again")
      setSaving(false)
      return
    }

    Alert.show('Notice', "Updated successfully")
    setSaving(false);
    setConfirmingDeleteId(false)
    setUnsavedIds([])
    setEditSponsorships(response.data)
  }

  const updateDonors = (data) => {
    return HTTP.patch(`/b/sponsors/${donorId}`, data)
  }

  const saveCommunityMembers = () => {
    let memberIds = []
    editSponsorships.map(beneficiary => memberIds.push(beneficiary.beneficiary_id))
    HTTP.patch(`/community/membership/${donorId}`, {
      member_ids: memberIds
    })
      .then((response) => {
        Alert.show("notice", "Updated successfully");
        setUnsavedIds([])
        setEditSponsorships(response.data)
        setSaving(false)
        setConfirmingDeleteId(false)
      })
      .catch((e) => {
        Alert.show("error", "Could not update, try again");
        setSaving(false);
      });
  }

  const saveProjectDonor = () => {
    let projectIds = []
    editSponsorships.map(beneficiary => projectIds.push(beneficiary.beneficiary_id))
    HTTP.patch(`/pro/sponsor/${donorId}`, {
      project_ids: projectIds
    })
      .then((response) => {
        Alert.show("notice", "Updated successfully");
        setUnsavedIds([])
        setEditSponsorships(response.data)
        setSaving(false)
        setConfirmingDeleteId(false)
      })
      .catch((e) => {
        Alert.show("error", "Could not update, try again");
        setSaving(false);
      });
  }

  const determineTemplate = (beneficiary) => {
    if (projectFromDonor) {
      return `${beneficiary.name}`
    }
    return `${beneficiary.first_name} ${beneficiary.last_name}`
  }

  const determineRoute = (id) => {
    if (forBeneficiary || withProjects) {
      return `/d/${id}/edit`
    }

    if (projectFromDonor) {
      return `/pro/edit/${id}`
    }

    if (forCommunities) {
      return `/b/${id}`
    }

    return `/b/${id}/edit`
  }

  useEffect(() => {}, [editSponsorships])

  useEffect(() => {
    if (editing && (withProjects || forManager || forCommunities)) {
      setEditSponsorships(totalSelection)
      return
    }

    if (!allUsers && (withProjects || forManager || forCommunities) && !editing) {
      setTotalSelection(editSponsorships)
      setEditSponsorships(editSponsorships.slice(0, 5))
    } else if (allUsers && (withProjects || forManager || forCommunities)) {
      setEditSponsorships(totalSelection)
    }
  }, [allUsers, editing])

  return (
    <div id="sponsorships-table" className="card">
      <div className="card-header flex-header">
        <div>
          <h4 className="card-title">{title}</h4>
          {showList && (withProjects || forManager || forCommunities) && !allUsers &&!editing &&
          <small class="form-text">Showing {editSponsorships.length} of {totalSelection.length} {title}</small>
          }
          {showList && (withProjects || forManager || forCommunities) && !editing && allUsers &&
          <small class="form-text">Showing all {editSponsorships.length} {title}</small>
          }
        </div>
        <div>
          {editing && hasEditPermission && (
            <div>
              <button
                onClick={cleanList}
                name="button"
                className="btn btn-outline-danger mr-1"
              >
                Close
              </button>
              {!forManager && !withProjects && !forBeneficiary && !projectFromDonor && !forCommunities &&
                <button onClick={save} name="button" className="btn btn-primary">
                {saving && <i className="fa fa-spinner-third fa-spin mr-2"></i>}
                Save
              </button>}

              {withProjects &&
                <button onClick={updateProjectSponsors} name="button" className="btn btn-primary">
                {saving && <i className="fa fa-spinner-third fa-spin mr-2"></i>}
                Save
              </button>}

              {forManager &&
                <button onClick={saveBeneficiaries} name="button" className="btn btn-primary">
                {saving && <i className="fa fa-spinner-third fa-spin mr-2"></i>}
                Save
              </button>}

              {forBeneficiary &&
                <button onClick={saveDonors} name="button" className="btn btn-primary">
                {saving && <i className="fa fa-spinner-third fa-spin mr-2"></i>}
                Save
              </button>}

              {projectFromDonor &&
                <button onClick={saveProjectDonor} name="button" className="btn btn-primary">
                {saving && <i className="fa fa-spinner-third fa-spin mr-2"></i>}
                Save
              </button>}

              {forCommunities &&
                <button onClick={saveCommunityMembers} name="button" className="btn btn-primary">
                {saving && <i className="fa fa-spinner-third fa-spin mr-2"></i>}
                Save
              </button>}
            </div>
          )}
          {!editing && (
            <>
            {(withProjects || forManager || forCommunities) &&
            <button onClick={() => setAllUsers(!allUsers)} name="button" className="btn btn-primary mr-2">
            {saving && <i className="fa fa-spinner-third fa-spin mr-2"></i>}
              {allUsers && <span>Hide {title}</span>} {!allUsers && <span>Show {title}</span>}
            </button>}

            
            {hasEditPermission &&
              <button
                onClick={() => {
                  setShowList(true)
                  setEditing(true)
                }}
                className="btn btn-primary select-beneficiary-button"
              >
                {" "}
                Edit {(forManager  || withProjects || forBeneficiary || projectFromDonor || forCommunities) && <span>{title}</span>}
                {!forManager && !withProjects && !forBeneficiary && !projectFromDonor && !forCommunities &&
                <span>
                  {translations.beneficiaries}
                </span>}
              </button>
            }
            </>
          )}
        </div>
      </div>

      {editing && !disabled && (
        <div className="form-group">
          {(forManager  || withProjects || forBeneficiary || projectFromDonor || forCommunities) && <label>{title}</label>}
          {!forManager && !withProjects && !forBeneficiary && !projectFromDonor && !forCommunities && <label>{translations.beneficiaries}</label>}
          <div className="input-group">
            <Select
              className="form-control select-beneficiaries"
              classNamePrefix="select-beneficiaries"
              onChange={(beneficiary) => setSelectedBeneficiary(beneficiary)}
              options={selectOptions}
              getOptionLabel={(beneficiary) =>
                `${determineTemplate(beneficiary)}`
              }
              getOptionValue={(beneficiary) => beneficiary.id}
              value={selectedBeneficiary}
            />
            <div className="input-group-append">
              <button
                onClick={() => addBeneficiary()}
                className="btn btn-primary"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && disabled && (
        <div className="form-group">
          {(forManager  || withProjects || forBeneficiary || projectFromDonor) && <label>{title}</label>}
          {!forManager && !withProjects && !forBeneficiary && !projectFromDonor && <label>{translations.beneficiaries}</label>}
          <div className="input-group">
            <Select
              className="form-control select-beneficiaries"
              classNamePrefix="select-beneficiaries"
              onChange={(beneficiary) => setSelectedBeneficiary(beneficiary)}
              options={selectOptions}
              getOptionLabel={(beneficiary) =>
                `${beneficiary.first_name} ${beneficiary.last_name}`
              }
              getOptionValue={(beneficiary) => beneficiary.id}
              value={selectedBeneficiary}
              isDisabled
            />
            <div className="input-group-append">
              <button
                onClick={() => addBeneficiary()}
                className="btn btn-primary"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

        {showList &&
        <div className="list-group list-group-flush">
        {editSponsorships.map((sponsorship) => (
          <div
            key={sponsorship.beneficiary_id}
            id={`sponsorship-beneficiary-${sponsorship.beneficiary_id}`}
            className="list-group-item"
          >
            <div className="list-group-item-body">
              <div className="list-group-item-body-main mb-2">
                <span className="mr-1">
                  {beneficiaryNameFromId(sponsorship.beneficiary_id)}
                </span>
                <a
                  href={
                    determineRoute(sponsorship.beneficiary_id)
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fa fa-external-link"></i>
                </a>
              </div>

              {!editing && !withProjects && !forManager && !forCommunities &&
                sponsorship.id &&
                sponsorship.custom_fields.map((custom_field) => (
                  <div key={custom_field.custom_team_field_id}>
                    <div className="">{custom_field.name}</div>
                    <div className="font-weight-bold mb-2">
                      {custom_field.value_type == 'link' &&
                        <a href={custom_field.value ? custom_field.value : '#'}>
                          {custom_field.anchor_text ? custom_field.anchor_text : custom_field.name}
                        </a>
                      }
                      {custom_field.value_type !== 'link' &&
                        <span>{custom_field.value}</span>
                      }
                    </div>
                  </div>
                ))}
            </div>
            {editing && (
              <div className="list-group-item-actions d-none-mobile">
                {!sponsorship.id && (
                  <span className="badge badge-sm badge-warning badge-pill mr-2">
                    Not Saved
                  </span>
                )}

                {(!sponsorship.id || confirmingDeleteId != sponsorship.id) && !disabled && (
                  <button
                    data-confirm="Are you sure? This can't be undone."
                    onClick={() => handleConfirmDelete(sponsorship)}
                    className="btn btn-sm btn-danger"
                  >
                    Remove
                  </button>
                )}

                {confirmingDeleteId && confirmingDeleteId == sponsorship.id && (
                  <span>
                    <button
                      onClick={() => handleDelete(sponsorship)}
                      className="btn btn-sm btn-danger mr-1"
                    >
                      Confirm Remove
                    </button>
                    <button
                      onClick={() => setConfirmingDeleteId(null)}
                      className="btn btn-sm btn-outline-danger"
                    >
                      Cancel
                    </button>
                  </span>
                )}
              </div>
            )}

            {!editing && !forManager && !withProjects && !forCommunities &&
              sponsorship.custom_fields.length > 0 &&
              sponsorship.id && (
                <div className="list-group-item-actions d-none-mobile">
                  <button
                    onClick={() =>
                      showCustomFieldsForm(sponsorship.beneficiary_id)
                    }
                    className="btn btn-sm btn-primary"
                  >
                    Edit Fields
                  </button>
                </div>
              )}
          </div>
        ))}

        {openCustomFieldsForm && (
          <SponsorshipForm
            sponsorship={editingSponsorship}
            openCustomFieldsForm={openCustomFieldsForm}
            setOpenCustomFieldsForm={setOpenCustomFieldsForm}
            updateSponsorship={updateSponsorship}
          />
        )}
      </div>}
    </div>
  );
}
